import React, {
  useMemo, useState, useEffect, useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { animateScroll as scroll } from 'react-scroll';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { Puck, createUsePuck } from '@puckeditor/core';
import {
  Button, Col, Container, Row,
} from 'reactstrap';

import {
  APIgetPage,
  APIcreatePage,
  APIupdatePage,
  APIdeletePage,
} from '../../api/Pages';
import { APIupdateSettings } from '../../api/Settings';
import { showModal } from '../../actions';

import { showConfirmationModal } from '../../actions/App';

import {
  arrayMove,
  parseResponseErrors,
  slugify,
  slugifyUrl,
} from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import {
  getClosedSections,
  getPageUrl,
  normalizePageVisibilityMode,
} from '../../helpers/PageSections';
import { getSetting } from '../../helpers/Settings';

import NothingFound from '../../components/NothingFound';
import { getPuckConfig } from '../../helpers/PuckConfig';
import {
  isAccessRestrictionPasswordValid,
  normalizeAccessRestriction,
} from '../../components/Page/Components/Common/AccessRestrictionItemsField';
import { PageIdContext } from '../../helpers/PageIdContext';
import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';

import AdminBreadcrumbs from '../../components/Admin/Breadcrumbs';
import AdminPageEditHeader from '../../components/Admin/PageEdit/Header';
import AdminPageEditTabContent from '../../components/Admin/PageEdit/TabContent';
import AdminPageEditSections from '../../components/Admin/PageEdit/Sections';
import AdminPageEditSidebar from '../../components/Admin/PageEdit/Sidebar';
import GoBackToListButton from '../../components/GoBackToListButton';

const usePuckSelector = createUsePuck();

const PuckHeaderActions = ({ onPublish, previewUrl }) => {
  const intl = useIntl();
  const dispatch = usePuckSelector((s) => s.dispatch);
  const puckUi = usePuckSelector((s) => s.appState.ui);
  const puckData = usePuckSelector((s) => s.appState.data);
  const isPageSettingsActive = !puckUi.selectedItem && !puckUi.itemSelector;
  const currentPageUrl = puckData?.root?.props?.url;
  const fullPageUrl = currentPageUrl
    ? getPageUrl({
      url: currentPageUrl,
      options: { page_type: puckData?.root?.props?.page_type || 'fullscreen' },
    }, true)
    : '';

  return (
    <>
      <CopyToClipboard
        text={fullPageUrl}
        options={{ format: 'text/plain' }}
        onCopy={() => addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The page link was copied to your clipboard!' }),
        })}
      >
        <Button
          color="link"
          size="sm"
          className="puck-settings-btn"
          title={intl.formatMessage({ defaultMessage: 'Copy page link' })}
          disabled={!fullPageUrl}
        >
          <i className="far fa-copy mr-1" />
          {intl.formatMessage({ defaultMessage: 'Copy link' })}
        </Button>
      </CopyToClipboard>
      <Button
        color="link"
        size="sm"
        className="puck-settings-btn"
        title={intl.formatMessage({ defaultMessage: 'Preview page' })}
        disabled={!previewUrl}
        onClick={() => {
          if (!previewUrl) return;
          window.open(previewUrl, '_blank', 'noopener,noreferrer');
        }}
      >
        <i className="far fa-eye mr-1" />
        {intl.formatMessage({ defaultMessage: 'Preview' })}
      </Button>
      <Button
        color={isPageSettingsActive ? 'secondary' : 'link'}
        size="sm"
        className="puck-settings-btn"
        title={intl.formatMessage({ defaultMessage: 'Page settings' })}
        // eslint-disable-next-line max-len
        onClick={() => dispatch({ type: 'setUi', ui: { ...puckUi, selectedItem: null, itemSelector: null } })}
      >
        <i className="far fa-cog mr-1" />
        {intl.formatMessage({ defaultMessage: 'Page settings' })}
      </Button>
      <Button
        color="primary"
        size="sm"
        onClick={() => onPublish(puckData)}
      >
        <i className="fas fa-save mr-1" />
        {intl.formatMessage({ defaultMessage: 'Save page' })}
      </Button>
    </>
  );
};

PuckHeaderActions.propTypes = {
  onPublish: PropTypes.func.isRequired,
  previewUrl: PropTypes.string,
};

PuckHeaderActions.defaultProps = {
  previewUrl: null,
};

const serializePuckData = (data) => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    return '';
  }
};

const sanitizeFontPickerForSave = (fontValue) => {
  if (!fontValue || typeof fontValue !== 'object' || Array.isArray(fontValue)) {
    return fontValue;
  }

  const sanitized = {
    ...fontValue,
  };

  delete sanitized.url;
  delete sanitized.googleSearch;

  return sanitized;
};

const DEFAULT_VISIBILITY_MODE = 'login_required';

const enforceVisibilityRules = (section, visibilityMode) => {
  if (visibilityMode !== 'homepage') {
    return section;
  }

  if (section.type === 'File') {
    return {
      ...section,
      allowDownload: false,
    };
  }

  if (section.type === 'CollectionFeed' || section.type === 'Collection') {
    return {
      ...section,
      allowCollectionDownload: false,
    };
  }

  return section;
};

const AdminPageEdit = ({
  match,
  history,
  handleDeletePage,
  openConfirmationModal,
  openModal,
}) => {
  const intl = useIntl();

  const config = useMemo(() => getPuckConfig(intl), [intl]);

  const { pageId } = match.params;

  const [page, setPage] = useState(null);
  const [urlEdited, setUrlEdited] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState([]);
  const [closedSections, setClosedSections] = useState([]);
  const [responseErrors, setResponseErrors] = useState({});
  const [is404, setIs404] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savedDataRef = useRef(null);
  const unblockNavigationRef = useRef(null);
  const usePuckEditor = true;

  useEffect(() => {
    document.body.classList.add('puck-editor-active');
    return () => {
      document.body.classList.remove('puck-editor-active');
    };
  }, []);

  const resetState = useCallback(() => {
    setIsDirty(false);
    setSaving(false);
    setErrors([]);
    setResponseErrors({});
  }, []);

  const handleErrors = useCallback((error) => {
    const response = error?.response;
    const urlError = response?.data?.errors?.['page.url']?.[0];

    setSaving(false);
    setResponseErrors(parseResponseErrors(response));

    if (urlError) {
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'This page URL is already in use by another page' }),
      });
    } else {
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
      });
    }
  }, [intl]);

  const handleCancelNewPageSetup = useCallback(() => {
    history.push('/admin/pages');
  }, [history]);

  const handleConfirmNewPageSetup = useCallback(
    draft => APIcreatePage({
      name: draft.name,
      url: draft.url,
      visibility_mode: draft.visibility_mode || 'login_required',
      template: draft.template || null,
    }).then((response) => {
      const newPageId = response?.data?.id;

      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'The new Content Portal has been created' }),
      });

      history.push(`/admin/pages/${newPageId}/edit`);
      return true;
    }),
    [history, intl],
  );

  const openNewPageSetupModal = useCallback(() => {
    openModal({
      className: 'modal-page-create',
      backdrop: 'static',
      initialValues: {
        name: '',
        url: '',
        visibility_mode: 'login_required',
      },
      onConfirm: handleConfirmNewPageSetup,
      onCancel: handleCancelNewPageSetup,
    });
  }, [openModal, handleConfirmNewPageSetup, handleCancelNewPageSetup]);

  const loadPage = useCallback(() => {
    setClosedSections(getClosedSections());

    if (!pageId) {
      setPage({
        name: '',
        url: '',
        sections: [],
        options: {
          visibility_mode: DEFAULT_VISIBILITY_MODE,
          logo_url: null,
          logo_url_preview: null,
        },
        timestamp: new Date(),
      });
      openNewPageSetupModal();
      return;
    }

    setLoading(true);

    APIgetPage(pageId, { edit_mode: true }).then((response) => {
      const { data } = response;

      if (data.password && data.options.access_restriction) {
        data.options.access_restriction.password = data.password;
      }

      if (data) {
        setPage({ ...data, timestamp: new Date() });
        setUrlEdited(true);
        setLoading(false);
      } else {
        setIs404(true);
        setLoading(false);
      }
    }).catch(() => {
      setIs404(true);
      setLoading(false);
    });
  }, [pageId, openNewPageSetupModal]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const updateSettings = useCallback((updatedClosedSections) => {
    APIupdateSettings({
      page_editor_settings: {
        ...getSetting('page_editor_settings'),
        closed_sections: updatedClosedSections,
      },
    });
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleSave = useCallback(() => {
    if (!page.name) {
      setErrors(['name']);
      return;
    }
    if (!page.url) {
      setErrors(['url']);
      return;
    }

    setSaving(true);
    setErrors([]);

    const pagePayload = { ...page };

    delete pagePayload.require_name;
    delete pagePayload.require_email;
    delete pagePayload.require_password;
    delete pagePayload.password;
    delete pagePayload.expires_at;

    if (pageId) {
      APIupdatePage(pagePayload).then(() => {
        resetState();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
      }).catch(handleErrors);
    } else {
      APIcreatePage(pagePayload).then((response) => {
        resetState();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The new Content Portal has been created' }),
        });
        const { data } = response;
        if (data && data.id) {
          history.push(`/admin/pages/${data.id}/edit`);
        }
      }).catch(handleErrors);
    }
  }, [page, pageId, intl, history, resetState, handleErrors]);

  // eslint-disable-next-line no-unused-vars
  const handleChange = useCallback((e) => {
    const { value, name } = e.target;

    setPage((prev) => {
      const updated = { ...prev };

      if (name === 'name') {
        updated.name = value;
      }

      if (name === 'url') {
        updated.url = slugifyUrl(value);
        setUrlEdited(true);
      }

      if (name === 'name' && !urlEdited) {
        updated.url = slugify(value);
      }

      if (['header', 'page_type', 'logo_url'].includes(name)) {
        updated.options = { ...updated.options, [name]: value };
      }

      return updated;
    });

    setIsDirty(true);
  }, [urlEdited]);

  // eslint-disable-next-line no-unused-vars
  const handleDelete = useCallback((e) => {
    e.preventDefault();
    handleDeletePage(page, () => {
      history.push('/admin/pages');
    });
  }, [page, history, handleDeletePage]);

  // eslint-disable-next-line no-unused-vars
  const handleAddSection = useCallback((section) => {
    const sectionId = uuidv4();
    const newSection = { id: sectionId, ...section };

    setPage((prev) => ({
      ...prev,
      sections: [...(prev.sections || []), newSection],
    }));
    setIsDirty(true);

    setTimeout(() => {
      const el = document.getElementById(`s-${sectionId}`);
      if (el) {
        scroll.scrollTo(el.offsetTop, {
          containerId: 'admin-custom-page',
          duration: 500,
          smooth: true,
        });
      }
    }, 0);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleChangeSection = useCallback((updatedSection) => {
    setPage((prev) => {
      const sections = [...prev.sections];
      const idx = sections.findIndex(s => s.id === updatedSection.id);
      if (idx >= 0) {
        sections[idx] = updatedSection;
      }
      return { ...prev, sections, timestamp: new Date() };
    });
    setIsDirty(true);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleDeleteSection = useCallback((sectionId) => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this section?' }),
      onConfirm: () => {
        setPage((prev) => ({
          ...prev,
          sections: prev.sections.filter(s => s.id !== sectionId),
        }));
        setIsDirty(true);
        return Promise.resolve();
      },
    };
    openConfirmationModal(modalParams);
  }, [intl, openConfirmationModal]);

  // eslint-disable-next-line no-unused-vars
  const handleMoveSection = useCallback((dragIndex, hoverIndex) => {
    setPage((prev) => {
      const fmi = prev.sections.findIndex(s => s.id === dragIndex);
      const smi = prev.sections.findIndex(s => s.id === hoverIndex);
      if (fmi < 0 || smi < 0) return prev;
      return {
        ...prev,
        sections: arrayMove(prev.sections, fmi, smi),
      };
    });
    setIsDirty(true);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleMoveSectionByIndex = useCallback((oldIndex, newIndex) => {
    setPage((prev) => {
      const dragIndex = prev.sections[oldIndex].id;
      const hoverIndex = prev.sections[newIndex].id;
      const fmi = prev.sections.findIndex(s => s.id === dragIndex);
      const smi = prev.sections.findIndex(s => s.id === hoverIndex);
      if (fmi < 0 || smi < 0) return prev;
      return {
        ...prev,
        sections: arrayMove(prev.sections, fmi, smi),
      };
    });
    setIsDirty(true);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleSwitchSection = useCallback((sectionId) => {
    setClosedSections((prev) => {
      const updated = prev.includes(sectionId)
        ? prev.filter(cs => cs !== sectionId)
        : [...prev, sectionId];
      updateSettings(updated);
      return updated;
    });
  }, [updateSettings]);

  const puckData = useMemo(() => ({
    content: (page?.sections || [])
      .filter(section => section.type && config.components[section.type])
      .map(section => ({
        type: section.type,
        props: { id: section.id, ...section },
      })),
    zones: page?.options?.puck_zones || {},
    root: {
      props: {
        name: page?.name || '',
        url: page?.url || '',
        access_restriction: normalizeAccessRestriction(page?.options?.access_restriction),
        pageNavigation: page?.options?.pageNavigation || page?.options?.page_navigation || [],
        visibility_mode: normalizePageVisibilityMode(
          page?.options?.visibility_mode ?? page?.visibility_mode,
        ),
        header: page?.options?.header || 'transparent',
        page_type: page?.options?.page_type || 'fullscreen',
        showSearchBar: page?.options?.showSearchBar ?? page?.options?.show_search_bar ?? true,
        accentColor: {
          mode: page?.options?.accent_color_mode === 'custom' ? 'custom' : 'workspace',
          color: page?.options?.accent_color_custom || '#203444',
        },
        logo_url: page?.options?.logo_url || '',
        fontPrimary: page?.options?.font_primary || '',
        fontSecondary: page?.options?.font_secondary || '',
        // eslint-disable-next-line no-underscore-dangle
        _bg: page?.options?._bg || {
          type: 'none', color: '#ffffff', gradientStart: '#ffffff', gradientEnd: '#000000', gradientDeg: 135,
        },
      },
    },
  }), [page, config.components]);

  const serializedPuckData = useMemo(() => serializePuckData(puckData), [puckData]);
  const unsavedChangesMessage = intl.formatMessage(
    {
      defaultMessage: 'You have unsaved changes on your Content Portal. Are you sure you want to leave?',
    },
  );

  useEffect(() => {
    if (!isDirty) {
      savedDataRef.current = serializedPuckData;
    }
  }, [serializedPuckData, isDirty]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) {
        return;
      }

      const unloadEvent = event;
      unloadEvent.preventDefault();
      unloadEvent.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      if (unblockNavigationRef.current) {
        unblockNavigationRef.current();
        unblockNavigationRef.current = null;
      }
      return undefined;
    }

    if (unblockNavigationRef.current) {
      unblockNavigationRef.current();
      unblockNavigationRef.current = null;
    }

    unblockNavigationRef.current = history.block(() => {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(unsavedChangesMessage);
      if (!confirmed) {
        return false;
      }

      if (unblockNavigationRef.current) {
        const unblock = unblockNavigationRef.current;
        unblockNavigationRef.current = null;
        unblock();
      }

      return true;
    });

    return () => {
      if (unblockNavigationRef.current) {
        unblockNavigationRef.current();
        unblockNavigationRef.current = null;
      }
    };
  }, [history, isDirty, unsavedChangesMessage]);

  const handlePuckPublish = useCallback((data) => {
    const rootProps = data?.root?.props || {};
    const accessRestriction = normalizeAccessRestriction(rootProps.access_restriction);

    if (!isAccessRestrictionPasswordValid(accessRestriction)) {
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'Password must be at least 3 characters' }),
      });
      return;
    }

    const visibilityMode = normalizePageVisibilityMode(
      rootProps.visibility_mode,
    );
    const pagePayload = { ...page };

    const updatedSections = data.content
      .map(item => ({
        id: item.props.id,
        type: item.type,
        page_visibility_mode: visibilityMode,
        ...item.props,
      }))
      .map(section => enforceVisibilityRules(section, visibilityMode));

    const accentColor = data.root.props.accentColor || {};
    const accentColorMode = accentColor.mode === 'custom' ? 'custom' : 'workspace';
    const accentColorCustom = accentColor.color || '#203444';

    const updatedPage = {
      ...pagePayload,
      name: data.root.props.name,
      url: data.root.props.url,
      visibility_mode: visibilityMode,
      options: {
        ...page.options,
        visibility_mode: visibilityMode,
        header: data.root.props.header,
        page_type: data.root.props.page_type,
        access_restriction: accessRestriction,
        pageNavigation: Array.isArray(data.root.props.pageNavigation)
          ? data.root.props.pageNavigation
          : [],
        showSearchBar: data.root.props.showSearchBar !== undefined
          ? data.root.props.showSearchBar
          : true,
        accent_color_mode: accentColorMode,
        accent_color_custom: accentColorCustom,
        logo_url: data.root.props.logo_url,
        font_primary: sanitizeFontPickerForSave(data.root.props.fontPrimary),
        font_secondary: sanitizeFontPickerForSave(data.root.props.fontSecondary),
        // eslint-disable-next-line no-underscore-dangle
        _bg: data.root.props._bg,
        puck_zones: data.zones || {},
      },
      sections: updatedSections,
    };

    setPage(updatedPage);
    setIsDirty(true);

    if (!updatedPage.name || !updatedPage.url) {
      const missing = [];
      if (!updatedPage.name) missing.push(intl.formatMessage({ defaultMessage: 'Page name' }));
      if (!updatedPage.url) missing.push(intl.formatMessage({ defaultMessage: 'Page URL' }));
      setErrors([!updatedPage.name && 'name', !updatedPage.url && 'url'].filter(Boolean));
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage(
          { defaultMessage: 'Required fields are missing: {fields}. Open page settings to fill them in.' },
          { fields: missing.join(', ') },
        ),
      });
      return;
    }

    setSaving(true);
    setErrors([]);

    if (pageId) {
      APIupdatePage(updatedPage).then(() => {
        resetState();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
      }).catch(handleErrors);
    } else {
      APIcreatePage(updatedPage).then((response) => {
        resetState();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The new Content Portal has been created' }),
        });
        const { data: responseData } = response;
        if (responseData && responseData.id) {
          history.push(`/admin/pages/${responseData.id}/edit`);
        }
      }).catch(handleErrors);
    }
  }, [page, pageId, intl, history, resetState, handleErrors]);

  const pageContextValue = useMemo(() => ({
    pageId,
    isEditing: true,
    visibilityMode: page?.visibility_mode || page?.options?.visibility_mode || null,
    allowedCollectionUuids: page?.allowed_collection_uuids || [],
    allowedFileUuids: page?.allowed_file_uuids || [],
  }), [pageId, page]);
  const previewUrl = page ? `/pages/${page.url}` : null;

  if (is404) {
    return <NothingFound />;
  }

  // eslint-disable-next-line no-unused-vars
  const sharedState = {
    page,
    pageId,
    urlEdited,
    isDirty,
    errors,
    closedSections,
    responseErrors,
    is404,
    loading,
    saving,
  };

  if (!loading && usePuckEditor && page.editor_type === 'puck') {
    // Read --primary from CSS variables and build a Puck azure override scale
    const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() || '#203444';
    const puckColorOverride = `
      :root {
        --puck-color-azure-01: color-mix(in srgb, ${primaryColor} 100%, #000 30%);
        --puck-color-azure-02: color-mix(in srgb, ${primaryColor} 95%, #000 20%);
        --puck-color-azure-03: color-mix(in srgb, ${primaryColor} 90%, #000 10%);
        --puck-color-azure-04: ${primaryColor};
        --puck-color-azure-05: color-mix(in srgb, ${primaryColor} 80%, #fff 20%);
        --puck-color-azure-06: color-mix(in srgb, ${primaryColor} 65%, #fff 35%);
        --puck-color-azure-07: color-mix(in srgb, ${primaryColor} 50%, #fff 50%);
        --puck-color-azure-08: color-mix(in srgb, ${primaryColor} 35%, #fff 65%);
        --puck-color-azure-09: color-mix(in srgb, ${primaryColor} 20%, #fff 80%);
        --puck-color-azure-10: color-mix(in srgb, ${primaryColor} 12%, #fff 88%);
        --puck-color-azure-11: color-mix(in srgb, ${primaryColor} 7%, #fff 93%);
        --puck-color-azure-12: color-mix(in srgb, ${primaryColor} 3%, #fff 97%);
      }
      [class*="_Input-input_"] {
        border-radius: 8px !important;
        padding: 0.375rem 0.75rem;
      }
      [class*="_DrawerItem-draggable_"] {
        border-radius: var(--border-radius, 4px) !important;
      }
      #admin-custom-page [class*="_PuckLayout_"] {
        user-select: none;
      }
      #admin-custom-page [class*="_PuckLayout-inner_"] {
        --puck-side-nav-width: 0 !important;
        --puck-user-left-side-bar-width: 300px !important;
        --puck-frame-width: minmax(0, 1fr) !important;
        grid-template-columns: 0 var(--puck-sidebar-left-width) var(--puck-frame-width) var(--puck-sidebar-right-width);
      }
      #admin-custom-page [class*="_PuckFields-field_"] > * {
        width: 100%;
        max-width: 100%;
      }
      #admin-custom-page [class*="_PuckFields-field_"] [class*="_Input_"] {
        width: 100%;
        max-width: 100%;
      }
      #admin-custom-page [class*="_PuckFields-field_"] .form-control,
      #admin-custom-page [class*="_PuckFields-field_"] select,
      #admin-custom-page [class*="_PuckFields-field_"] textarea,
      #admin-custom-page [class*="_PuckFields-field_"] input:not([type='checkbox']):not([type='radio']) {
        width: 100%;
        max-width: 100%;
      }
      #admin-custom-page [class*="_PuckFields-field_"] label:has(input[type='radio']) {
        margin-bottom: 0;
      }
      [class*="_DraggableComponent_"] {
        user-select: none;
      }
      #admin-custom-page li[class*="_NavItem_"]:has(svg.lucide-layers) {
        display: none !important;
      }
      #admin-custom-page [class*="_PuckHeader_"] {
        padding-left: 0 !important;
      }
      #admin-custom-page [class*="_PuckHeader-inner_"] {
        border-left: 0 !important;
      }
      #admin-custom-page [class*="_DropZone_"] {
        --puck-slot-min-empty-height: 150px !important;
        --min-empty-height: 150px !important;
      }
    `;

    const adminContent = (
      <div id="admin-custom-page" className="main-content admin-custom-page-puck">
        <style>{puckColorOverride}</style>
        <div>
          <Puck
            config={config}
            data={puckData}
            headerTitle={page?.name || intl.formatMessage({ defaultMessage: 'Page' })}
            onPublish={handlePuckPublish}
            iframe={{ enabled: false }}
            overrides={{
              browserContext: {
                window: typeof window !== 'undefined' ? window : undefined,
                document: typeof document !== 'undefined' ? document : undefined,
              },
              headerActions: () => (
                <PuckHeaderActions
                  onPublish={handlePuckPublish}
                  previewUrl={previewUrl}
                />
              ),
            }}
            onChange={(data) => {
              setIsDirty(serializePuckData(data) !== savedDataRef.current);
            }}
          />
        </div>
      </div>
    );

    return (
      <PageIdContext.Provider
        value={pageContextValue}
      >
        {adminContent}
      </PageIdContext.Provider>
    );
  }

  if (!loading && page.editor_type !== 'puck') {
    return (
      <div id="admin-custom-page" className="main-content">
        <Container fluid>
          <ErrorBoundary>
            <AdminBreadcrumbs />
            <GoBackToListButton />
            <AdminPageEditHeader pageId={pageId} />
            <div className="content-wrapper">
              {loading ? (
                <Loading />
              ) : (
                <Row className="page-edit-wrapper">
                  <Col className="page-edit-content">
                    <ErrorBoundary>
                      <AdminPageEditTabContent
                        {...sharedState}
                        handleChange={handleChange}
                        handleSave={handleSave}
                        handleDelete={handleDelete}
                      />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AdminPageEditSections
                        {...sharedState}
                        handleChange={handleChange}
                        handleAddSection={handleAddSection}
                        handleChangeSection={handleChangeSection}
                        handleDeleteSection={handleDeleteSection}
                        handleMoveSection={handleMoveSection}
                        handleMoveSectionByIndex={handleMoveSectionByIndex}
                        handleSwitchSection={handleSwitchSection}
                      />
                    </ErrorBoundary>
                  </Col>
                  <Col xs="2" className="page-edit-sidebar">
                    <AdminPageEditSidebar
                      {...sharedState}
                      handleChange={handleChange}
                      handleDelete={handleDelete}
                      handleSave={handleSave}
                      handleAddSection={handleAddSection}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </ErrorBoundary>
        </Container>
      </div>
    );
  }

  return (
    <div className="center">

      <Loading />
    </div>
  );
};

AdminPageEdit.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  handleDeletePage: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, props) => ({
  handleDeletePage: (page, callback) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this Content Portal? The page will be permanently removed.' }),
      onConfirm: () => (
        APIdeletePage(page.id).then(() => {
          callback(page);
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  openModal: (params) => {
    dispatch(showModal('AdminPageCreateModal', params));
  },
});

export default withRouter(connect(null, mapDispatchToProps)(AdminPageEdit));
